/** Le tappe della prova guidata: una giornata intera, dal tavolo all'incasso.
 *  `fatto(s)` riceve lo stato osservato durante il percorso, non lo stato del locale: così un
 *  tavolo già occupato dai dati di prova non fa saltare tappe.
 *  Tre voci diverse, stessi gesti: titolare (guarda il locale), sala (guarda il turno),
 *  bancone (bar, tavola calda o fredda: si ordina al banco e si incassa subito).
 */
const inComanda = () => window.location.pathname.startsWith("/comanda/");

const TAVOLO = {
  id: "tavolo",
  dove: "/sala",
  doveTesto: "Tocca «Sala», in fondo allo schermo: ti porto dai tavoli.",
  sel: ["[data-testid^='table-']:not([data-testid^='table-res-'])"],
  selFuori: ["[data-testid='mobilenav-sala']", "[data-testid='nav-sala']"],
  fatto: inComanda,
};
const PIATTI = {
  id: "piatti",
  sel: ["[data-testid^='menu-item-']"],
  fatto: (s) => s.righe >= 2,
};
const CUCINA = {
  id: "cucina",
  sel: ["[data-testid='cart-bar-send-btn']", "[data-testid='send-checked-btn']"],
  unico: true,
  fatto: (s) => s.inviato,
};
const CONTO = {
  id: "conto",
  sel: ["[data-testid='conto-btn']", "[data-testid='ordinato-conto-btn']"],
  unico: true,
  fatto: (s) => s.conto,
};
const FONDO = {
  id: "fondo",
  dove: "/cassa",
  doveTesto: "Ti porto in «Cassa»: è la pagina dei soldi della giornata.",
  sel: ["[data-testid='cash-floats-panel']"],
  selFuori: ["[data-testid='mobilenav-cassa']", "[data-testid='nav-cassa']"],
  fatto: (s) => s.fondo >= 8,
};

// Il titolare guarda il locale; la sala guarda il proprio turno: stessi gesti, parole diverse.
const TITOLARE = [
  { ...TAVOLO,
    titolo: "Accomoda i clienti",
    testo: "Tocca un tavolo: si apre il conto e da quel momento tutto resta attaccato a quel tavolo.",
    aiuto: "In Sala trovi tutti i tavoli. Apri quello che vuoi: nella demo nessun cliente aspetta davvero." },
  { ...PIATTI,
    titolo: "Prendi l'ordinazione",
    testo: "Tocca due voci del listino: finiscono nella comanda, con il totale già calcolato.",
    aiuto: "Funziona uguale per un primo, una pizza, un tramezzino o un caffè: il listino è il tuo, categoria per categoria." },
  { ...CUCINA,
    titolo: "Manda ai reparti",
    testo: "Tocca «Manda»: l'ordinazione arriva a cucina, pizzeria o bancone e i tempi partono da soli.",
    aiuto: "Ogni reparto vede solo la sua parte sul proprio monitor: bancone, tavola calda, cucina. Niente foglietti e niente urla." },
  { ...CONTO,
    titolo: "Chiudi il conto",
    testo: "Apri il conto: il totale è pronto, si divide fra più persone e la cassa si aggiorna da sola.",
    aiuto: "Puoi applicare uno sconto, stampare il preconto e scegliere contanti o carta." },
  { ...FONDO,
    titolo: "Il fondo cassa dello staff",
    testo: "Qui assegni tu il fondo di inizio turno, a una persona o a tutta la squadra: poi vedi quanto ha in mano e quanto deve versare.",
    aiuto: "Il fondo lo decidi tu (o chi deleghi come direttore): il personale non se lo assegna da solo. A fine giornata la quadratura confronta contato e atteso e mostra subito la differenza." },
];

const SALA = [
  { ...TAVOLO,
    titolo: "Il tuo tavolo",
    testo: "Tocca il tavolo che stai servendo: si apre la comanda e resta a tuo nome.",
    aiuto: "Nei tavoli con il bollino sei tu quello che serve. Apri quello che vuoi: qui nessuno aspetta davvero." },
  { ...PIATTI,
    titolo: "Scrivi l'ordinazione",
    testo: "Tocca due voci mentre il cliente parla: puoi togliere un ingrediente o aggiungerne uno.",
    aiuto: "Cerca col nome o scegli la categoria. Se serve una scelta si apre una finestra: confermi e va dentro." },
  { ...CUCINA,
    titolo: "Mandala ai reparti",
    testo: "Tocca «Manda»: non devi correre a portare il foglietto, ci pensa lui.",
    aiuto: "Se una voce va servita dopo, mettila nel giro successivo: il reparto la prepara al momento giusto." },
  { ...CONTO,
    titolo: "Incassa e libera il tavolo",
    testo: "Apri il conto: puoi dividerlo fra le persone e segnare quanto hai incassato tu.",
    aiuto: "Gli incassi degli altri non li vedi: ognuno vede solo i suoi. Il fondo cassa te lo assegna il titolare, e qui vedi quanto devi versare." },
];

const BANCONE = [
  { ...TAVOLO,
    titolo: "Apri il banco o il tavolino",
    testo: "Tocca un tavolino, oppure il banco per chi consuma al volo: l'ordinazione resta lì fino all'incasso.",
    aiuto: "Al bar funziona in due modi: colazione al banco che si incassa subito, o tavolino che resta aperto fino alla fine." },
  { ...PIATTI,
    titolo: "Batti l'ordinazione",
    testo: "Tocca due voci: caffè, tramezzino, un piatto di tavola calda. Il totale si fa da solo.",
    aiuto: "Tavola calda e tavola fredda sono categorie come le altre: ci sono anche i piatti del giorno e le voci a peso." },
  { ...CUCINA,
    titolo: "Manda al bancone (o in cucina)",
    testo: "Tocca «Manda»: le bevande vanno al bancone, i piatti caldi alla cucina, ognuno sul suo monitor.",
    aiuto: "Se vuoi, le bevande escono sempre per prime: è un interruttore in Impostazioni, così il cliente non aspetta il caffè." },
  { ...CONTO,
    titolo: "Incassa in un tocco",
    testo: "Apri il conto: contanti o carta, e la cassa della giornata si aggiorna subito.",
    aiuto: "Per il cliente abituale puoi anche dividere il conto o segnarlo. Il tuo fondo cassa lo assegna il titolare e qui vedi quanto devi versare." },
];

export const passiPer = (ruolo) =>
  ruolo === "titolare" || ruolo === "direttore" ? TITOLARE
  : ruolo === "barista" ? BANCONE
  : SALA;

export const PASSI = TITOLARE;
